'use client'

import React, { useEffect, useRef, useState } from 'react'
import { motion, useAnimation, useInView, Variants } from 'framer-motion'

// Animation variants for different types of animations
export const fadeInVariants: Variants = {
  hidden: { 
    opacity: 0 
  },
  visible: { 
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
}

export const slideUpVariants: Variants = {
  hidden: { 
    opacity: 0, 
    y: 20 
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
}

export const scaleInVariants: Variants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95 
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
}

export const slideInLeftVariants: Variants = {
  hidden: { 
    opacity: 0, 
    x: -20 
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
}

export const slideInRightVariants: Variants = {
  hidden: { 
    opacity: 0, 
    x: 20 
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
}

// Stagger container for animating multiple children
export const staggerContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
}

interface ScrollAnimationProps {
  children: React.ReactNode
  variants?: Variants
  className?: string
  delay?: number
  threshold?: number
  once?: boolean
}

// Main scroll animation component
export function ScrollAnimation({ 
  children, 
  variants = fadeInVariants, 
  className = "",
  delay = 0,
  threshold = 0.1,
  once = true
}: ScrollAnimationProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { 
    threshold,
    once 
  })
  const controls = useAnimation()

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        controls.start("visible")
      }, delay * 1000)
      return () => clearTimeout(timer)
    }
  }, [isInView, controls, delay])

  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      variants={variants}
      className={className}
    >
      {children}
    </motion.div>
  )
}

// Stagger animation for lists
interface StaggerAnimationProps {
  children: React.ReactNode
  className?: string
  delay?: number
  staggerDelay?: number
  threshold?: number
  once?: boolean
}

export function StaggerAnimation({ 
  children, 
  className = "",
  delay = 0,
  staggerDelay = 0.1,
  threshold = 0.1,
  once = true
}: StaggerAnimationProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { 
    threshold,
    once 
  })
  const controls = useAnimation()

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: delay
      }
    }
  }

  useEffect(() => {
    if (isInView) {
      controls.start("visible")
    }
  }, [isInView, controls])

  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      variants={containerVariants}
      className={className}
    >
      {children}
    </motion.div>
  )
}

// Number counting animation
interface CountingAnimationProps {
  from: number
  to: number
  duration?: number
  className?: string
  suffix?: string
  prefix?: string
  threshold?: number
  once?: boolean
}

export function CountingAnimation({
  from,
  to,
  duration = 2,
  className = "",
  suffix = "",
  prefix = "",
  threshold = 0.3,
  once = true
}: CountingAnimationProps) {
  const [count, setCount] = useState(from)
  const ref = useRef(null)
  const isInView = useInView(ref, { threshold, once })

  useEffect(() => {
    if (!isInView) return

    const startTime = Date.now()
    const endTime = startTime + duration * 1000
    
    const updateCount = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / (endTime - startTime), 1)
      
      // Easing function for smooth counting
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
      const easedProgress = easeOutCubic(progress)
      
      const currentCount = Math.round(from + (to - from) * easedProgress)
      setCount(currentCount)
      
      if (progress < 1) {
        requestAnimationFrame(updateCount)
      }
    }
    
    requestAnimationFrame(updateCount)
  }, [isInView, from, to, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  )
}

// Pre-defined animation components for common use cases
export const FadeIn: React.FC<Omit<ScrollAnimationProps, 'variants'>> = (props) => (
  <ScrollAnimation variants={fadeInVariants} {...props} />
)

export const SlideUp: React.FC<Omit<ScrollAnimationProps, 'variants'>> = (props) => (
  <ScrollAnimation variants={slideUpVariants} {...props} />
)

export const ScaleIn: React.FC<Omit<ScrollAnimationProps, 'variants'>> = (props) => (
  <ScrollAnimation variants={scaleInVariants} {...props} />
)

export const SlideInLeft: React.FC<Omit<ScrollAnimationProps, 'variants'>> = (props) => (
  <ScrollAnimation variants={slideInLeftVariants} {...props} />
)

export const SlideInRight: React.FC<Omit<ScrollAnimationProps, 'variants'>> = (props) => (
  <ScrollAnimation variants={slideInRightVariants} {...props} />
)

// Hover effects for cards and interactive elements
interface HoverCardProps {
  children: React.ReactNode
  className?: string
  hoverScale?: number
  hoverY?: number
}

export function HoverCard({ 
  children, 
  className = "",
  hoverScale = 1.02,
  hoverY = -2
}: HoverCardProps) {
  return (
    <motion.div
      whileHover={{ 
        scale: hoverScale, 
        y: hoverY,
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
      }}
      transition={{ 
        duration: 0.2, 
        ease: "easeOut" 
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

// Respect user's motion preferences
export function MotionConfig({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1 }}
      transition={{ duration: 0 }}
    >
      {children}
    </motion.div>
  )
}